/* eslint-disable @typescript-eslint/no-explicit-any */
interface AwsSdkV3Model {
  smithy?: string;
  shapes?: Record<string, any>;
}

interface AwsSdkV2Model {
  version: string;
  operations: Record<string, any>;
  shapes: Record<string, any>;
}

interface Shape {
  type: string;
  traits?: Record<string, any>;
  members?: Record<string, any>;
  member?: { target: string };
  key?: { target: string };
  value?: { target: string };
  input?: { target: string };
  output?: { target: string };
  operations?: Array<{ target: string }>;
  version?: string;
}

/**
 * Converts AWS SDK v3 Smithy model to v2 JSON format
 */
export const convertAwsModelV3ToV2 = (v3Model: AwsSdkV3Model): AwsSdkV2Model => {
  const shapes = v3Model.shapes || {};

  // Find service shape
  const serviceShapeId = Object.keys(shapes).find((id) => shapes[id].type === 'service');

  if (!serviceShapeId) {
    throw new Error('No service shape found in v3 model');
  }

  const serviceShape = shapes[serviceShapeId];

  // Convert operations
  const operations: Record<string, any> = {};
  if (serviceShape.operations) {
    serviceShape.operations.forEach((op: any) => {
      const opName = op.target.split('#')[1];
      const opShape = shapes[op.target];
      if (opShape) {
        operations[opName] = convertOperation(opShape);
      }
    });
  }

  // Convert shapes
  const v2Shapes: Record<string, any> = {};
  Object.keys(shapes).forEach((shapeId) => {
    const shapeName = shapeId.split('#')[1];
    const shape = shapes[shapeId];

    // Skip service shape
    if (shape.type === 'service') return;

    v2Shapes[shapeName as string] = convertShape(shape);

    // hacky string shape needed (not provided by sdk template)
    v2Shapes['String'] = { type: 'string' };
    v2Shapes['Timestamp'] = { type: 'timestamp' };
  });

  return {
    version: '2.0',
    operations,
    shapes: v2Shapes,
  };
};

/**
 * Convert operation
 */
const convertOperation = (opShape: Shape): any => {
  const op: any = {
    name: opShape.traits?.['smithy.api#operationName'] || opShape.traits?.['aws.api#operation']?.name,
  };

  // Input
  if (opShape.input?.target) {
    op.input = { shape: opShape.input.target.split('#')[1] };
  }

  // Output
  if (opShape.output?.target) {
    op.output = { shape: opShape.output.target.split('#')[1] };
  }

  // Documentation
  if (opShape.traits?.['smithy.api#documentation']) {
    op.documentation = opShape.traits['smithy.api#documentation'];
  }

  return op;
};

/**
 * Convert shape
 */
const convertShape = (shape: Shape): any => {
  const v2Shape: any = {
    type: mapShapeType(shape.type),
  };

  // Documentation
  if (shape.traits?.['smithy.api#documentation']) {
    v2Shape.documentation = shape.traits['smithy.api#documentation'];
  }

  // Required fields
  if (shape.traits?.['smithy.api#required']) {
    v2Shape.required = shape.traits['smithy.api#required'];
  }

  switch (shape.type) {
    case 'structure':
      return convertStructure(shape, v2Shape);
    case 'list':
      return convertList(shape, v2Shape);
    case 'map':
      return convertMap(shape, v2Shape);
    case 'string':
      return convertString(shape, v2Shape);
    case 'integer':
    case 'long':
    case 'float':
    case 'double':
    case 'boolean':
    case 'timestamp':
      return convertPrimitive(shape, v2Shape);
    case 'blob':
      v2Shape.type = 'blob';
      return v2Shape;
    case 'enum':
      v2Shape.type = 'string';
      if (shape.members) {
        v2Shape.enum = Object.keys(shape.members);
      }
      return v2Shape;
    default:
      return v2Shape;
  }
};

/**
 * Map v3 shape type to v2
 */
const mapShapeType = (type: string): string => {
  const typeMap: Record<string, string> = {
    structure: 'structure',
    list: 'list',
    map: 'map',
    string: 'string',
    String: 'string',
    integer: 'integer',
    long: 'long',
    float: 'float',
    double: 'double',
    boolean: 'boolean',
    timestamp: 'timestamp',
    blob: 'blob',
  };
  return typeMap[type] || type;
};

/**
 * Convert structure shape
 */
const convertStructure = (shape: Shape, v2Shape: any): any => {
  if (shape.members) {
    v2Shape.members = {};

    // Collect required members
    const required: string[] = [];

    Object.keys(shape.members).forEach((memberName) => {
      const member = shape.members?.[memberName];
      const memberShape: any = {
        shape: member.target.split('#')[1],
      };

      // Documentation
      if (member.traits?.['smithy.api#documentation']) {
        memberShape.documentation = member.traits['smithy.api#documentation'];
      }

      // Required
      if (member.traits?.['smithy.api#required']) {
        required.push(memberName);
      }

      if (member.traits?.['smithy.api#String']) {
        required.push(memberName);
      }

      v2Shape.members[memberName] = memberShape;
    });

    if (required.length > 0) {
      v2Shape.required = required;
    }
  }

  return v2Shape;
};

/**
 * Convert list shape
 */
const convertList = (shape: Shape, v2Shape: any): any => {
  if (shape.member?.target) {
    v2Shape.member = { shape: shape.member.target.split('#')[1] };
  }
  return v2Shape;
};

/**
 * Convert map shape
 */
const convertMap = (shape: Shape, v2Shape: any): any => {
  if (shape.key?.target) {
    v2Shape.key = { shape: shape.key.target.split('#')[1] };
  }
  if (shape.value?.target) {
    v2Shape.value = { shape: shape.value.target.split('#')[1] };
  }
  return v2Shape;
};

/**
 * Convert string shape
 */
const convertString = (shape: Shape, v2Shape: any): any => {
  // Enum
  if (shape.traits?.['smithy.api#enum']) {
    v2Shape.enum = shape.traits['smithy.api#enum'].map((e: any) => e.value);
  }

  // Length
  if (shape.traits?.['smithy.api#length']) {
    const length = shape.traits['smithy.api#length'];
    if (length.min !== undefined) v2Shape.min = length.min;
    if (length.max !== undefined) v2Shape.max = length.max;
  }

  // Pattern
  if (shape.traits?.['smithy.api#pattern']) {
    v2Shape.pattern = shape.traits['smithy.api#pattern'];
  }

  return v2Shape;
};

/**
 * Convert primitive shape
 */
const convertPrimitive = (shape: Shape, v2Shape: any): any => {
  // Range for numeric types
  if (shape.traits?.['smithy.api#range']) {
    const range = shape.traits['smithy.api#range'];
    if (range.min !== undefined) v2Shape.min = range.min;
    if (range.max !== undefined) v2Shape.max = range.max;
  }

  return v2Shape;
};
